import React, { createContext, useContext } from 'react'
import { CanvasElementMenu, type CanvasElementMenuAvailability } from './CanvasElementMenu'
import type { ZOrderDirection } from './utils/canvasZOrder'

/**
 * Was das Menü am Rechtsklick von der Pinnwand braucht. `CanvasFlow` legt es
 * in den Context — als EIN stabiles Objekt, dessen Funktionen über Refs lesen,
 * damit kein Element neu zeichnet, wenn sich Auswahl oder Werkzeug ändern.
 */
export interface CanvasElementMenuApi {
  /** Vor dem Öffnen: `false` hält das Menü zu. Wählt das Element aus, falls es nicht dazugehört. */
  beforeOpen: (id: string) => boolean
  availability: () => CanvasElementMenuAvailability
  apply: (direction: ZOrderDirection) => void
  /** Den Fokus nach dem Schließen zurück auf das Element — in den Canvas, wo ⌘Z und ⌫ horchen. */
  closeAutoFocus: (id: string, event: Event) => void
}

const CanvasElementMenuContext = createContext<CanvasElementMenuApi | null>(null)

export const CanvasElementMenuProvider = CanvasElementMenuContext.Provider

/**
 * Legt das Menü um eine Elementart der Pinnwand (`NODE_TYPES` in `CanvasFlow`).
 *
 * Um jede Art einzeln und nicht einmal um die ganze Fläche: React Flows Fläche
 * hält `contextmenu` auf (Rechtsziehen verschiebt dort), der Auslöser muss
 * also zwischen Element und Fläche sitzen. Ohne Provider — in Stories und
 * Tests der Elemente — bleibt die Art, wie sie ist.
 */
export function withCanvasElementMenu<P extends { id: string }>(Node: React.ComponentType<P>) {
  const WithMenu: React.FC<P> = props => {
    const api = useContext(CanvasElementMenuContext)
    if (!api) return <Node {...props} />
    return (
      <CanvasElementMenu
        onBeforeOpen={event => {
          /*
            Nur ein Rechtsklick AUF das Element. Die Options-Leiste hängt als
            Portal außerhalb des Nodes, gehört im React-Baum aber zu ihm — ihr
            `contextmenu` stiege bis hierher und öffnete das Menü für ein
            Element, auf das niemand geklickt hat (im E2E über einem älteren
            Zettel, den sie halb verdeckte).
          */
          const onNode = (event.target as Element).closest?.('.react-flow__node')?.getAttribute('data-id') === props.id
          return onNode && api.beforeOpen(props.id)
        }}
        getAvailability={api.availability}
        onSelect={api.apply}
        onCloseAutoFocus={event => api.closeAutoFocus(props.id, event)}
      >
        <Node {...props} />
      </CanvasElementMenu>
    )
  }
  WithMenu.displayName = `WithCanvasElementMenu(${Node.displayName ?? Node.name})`
  return WithMenu
}
